import { Chip, Link } from "@heroui/react";

import { AccountBadge } from "@/src/components/AccountBadge";
import { EventStatusChip } from "@/src/components/EventStatusChip";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { filledPlaces } from "@/src/pages/Events/eventPlaces";
import type { AdminEvent } from "@/src/pages/Events/events.type";
import { ROUTES } from "@/src/routes/paths";

/**
 * Une ligne de la liste des événements.
 *
 * ⚠️ **Toute la ligne est cliquable, mais c'est le nom qui porte le lien** —
 * même montage que `AccountRow`, et le `static` y est tout aussi indispensable :
 * `.link` pose `position: relative`, et sans lui l'`after:inset-0` s'ancrerait
 * sur le lien au lieu de la ligne.
 *
 * ⚠️ **L'organisateur est un lien à part**, imbriqué dans la surface étirée.
 * C'est voulu : depuis une liste d'événements, « qui a créé ça » mène souvent
 * plus vite à une décision que l'événement lui-même. Il est posé au-dessus de la
 * surface (`relative z-10`) pour rester atteignable.
 */
export function EventRow({ event }: { event: AdminEvent }) {
  const t = useTranslations("events");
  const tm = useTranslations("moderation");
  const tg = useTranslations("geo");
  const { locale } = useLocale();

  const scheduled =
    event.date === null
      ? null
      : new Intl.DateTimeFormat(locale, {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(event.date));

  const pending = event.pendingParticipants.length;

  return (
    <div className="relative flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg p-3 transition-colors hover:bg-surface-hover">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            className="static text-sm font-medium after:absolute after:inset-0"
            href={ROUTES.event(event.id)}
          >
            {event.name}
          </Link>

          <EventStatusChip status={event.status} />

          {/* ⚠️ **Retiré n'est pas fermé.** Un événement clos reste visible de
              tous ; celui-ci n'est visible que d'ici, et c'est ce que la liste
              doit dire avant qu'on ouvre la fiche. */}
          {event.unapprovedAt ? (
            <Chip color="danger" size="sm">
              {tm("event-state-hidden")}
            </Chip>
          ) : null}

          {/* Un dossier en attente est ce qui appelle une action : il se voit
              sans ouvrir la fiche. */}
          {pending > 0 ? (
            <Chip color="accent" size="sm" variant="secondary">
              {t("pending-count", { count: pending })}
            </Chip>
          ) : null}

          {/* Une adresse que l'organisateur a choisi de ne pas publier : la
              modération la voit, mais doit savoir qu'elle est privée. */}
          {event.isLocationPublic ? null : (
            <Chip color="warning" size="sm" variant="secondary">
              {t("location-hidden")}
            </Chip>
          )}
        </div>

        <div className="relative z-10 flex w-fit min-w-0 items-center gap-2 text-xs text-muted">
          <AccountBadge account={event.creatorProfile} isLinked />
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-0.5 text-xs text-muted">
        <span>{scheduled ?? t("no-date")}</span>

        <span className="tabular-nums">
          {t("places-filled", {
            filled: filledPlaces(event),
            places: event.places,
          })}
        </span>

        {/* La distance ne vaut que si une recherche géographique l'a produite ;
            ailleurs le serveur la met à `null`, et non à zéro. */}
        <span className="max-w-64 truncate">
          {event.distance === null
            ? (event.locationLabel ?? t("no-location"))
            : `${tg("distance", { km: Math.round(event.distance) })}${
                event.locationLabel ? ` · ${event.locationLabel}` : ""
              }`}
        </span>
      </div>
    </div>
  );
}
